import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import FormattedCurrency from 'components/ui/FormattedCurrency/FormattedCurrency';
import { passengerTypes } from 'types/passengers';

import { getLoyaltyTicketRefundAmount, getRefundAmount } from '../PrrRefund.utils';

import { BodyCell, Row, StyledTable, StyledTableBody, StyledTableFooter, StyledTableHead } from './CompensationBreakdown.styles';

const GROUP_FIELD_KEY = 'bucketCode';
const PASSENGER_TYPE_KEY = 'passengerType';
const PRICE_KEY = 'price';
const LOYALTY_POINTS_KEY = 'loyaltyPoints';

const NonSalesforceCompensationBreakdown = ({ isVoucher, isLoyalty, currency, compensateLegs, refundPercentage }) => {
  let ticketPrice = 0;
  let refundAmount = 0;

  const compensateLegsWithGroupedProducts = compensateLegs.map((leg) => ({
    ...leg,
    products: leg.products.reduce(
      (result, item) => ({
        ...result,
        [`${item[PASSENGER_TYPE_KEY]}_${item[GROUP_FIELD_KEY]}`]: [
          ...(result[`${item[PASSENGER_TYPE_KEY]}_${item[GROUP_FIELD_KEY]}`] || []),
          item
        ]
      }),
      {}
    )
  }));

  return (
    <StyledTable data-testid="compensation-table">
      <StyledTableHead>
        <tr>
          <th />
          <th>
            <FormattedMessage id="prrRefund.passengerType" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.passengersCompensated" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.costPerPerson" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.total" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.compensationPerPerson" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.totalCompensation" />
          </th>
        </tr>
      </StyledTableHead>
      <StyledTableBody>
        {compensateLegsWithGroupedProducts.map(({ legName, products }) => {
          const passengerTypeRows = [];
          const passengersCompensatedRows = [];
          const costPerPersonRows = [];
          const totalRows = [];
          const compensationPerPersonRows = [];
          const totalCompensationRows = [];
          const currencyRows = [];

          Object.keys(products).forEach((key, index) => {
            const groupedProducts = products[key];

            const pricePerPassenger = groupedProducts[0][isLoyalty ? LOYALTY_POINTS_KEY : PRICE_KEY];
            const bucketCodePerPassenger = groupedProducts[0].bucketCode;
            const compensationPerPassenger =
              isVoucher && isLoyalty
                ? getLoyaltyTicketRefundAmount(bucketCodePerPassenger, refundPercentage, currency)
                : getRefundAmount(pricePerPassenger, refundPercentage);

            const total = pricePerPassenger * groupedProducts.length;
            const totalCompensation = compensationPerPassenger * groupedProducts.length;

            ticketPrice += total;
            refundAmount += totalCompensation;

            passengerTypeRows.splice(index, 0, groupedProducts[0].passengerType);
            passengersCompensatedRows.splice(index, 0, groupedProducts.length);
            costPerPersonRows.splice(index, 0, pricePerPassenger);
            totalRows.splice(index, 0, total);
            compensationPerPersonRows.splice(index, 0, compensationPerPassenger);
            totalCompensationRows.splice(index, 0, totalCompensation);
            currencyRows.splice(index, 0, groupedProducts[0].currency);
          });

          return (
            <tr data-testid={legName} key={legName}>
              <td>
                <Row style={{ justifyContent: 'center' }}>{legName}</Row>
              </td>
              <td>
                <BodyCell>
                  {passengerTypeRows.map((type, index) => (
                    <Row key={`${index}-${type}-${legName}`}>
                      <FormattedMessage id={`passengerTypes.${passengerTypes[type]}`} />
                    </Row>
                  ))}
                </BodyCell>
              </td>
              <td>
                <BodyCell>
                  {passengersCompensatedRows.map((passengersNumber, index) => (
                    <Row key={`${index}-${passengersNumber}-${legName}`}>{passengersNumber}</Row>
                  ))}
                </BodyCell>
              </td>
              <td>
                <BodyCell>
                  {costPerPersonRows.map((costPerPerson, index) => (
                    <Row key={`${index}-${costPerPerson}-${legName}`}>
                      {isLoyalty ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: costPerPerson }} />
                      ) : (
                        <FormattedCurrency currency={currencyRows?.[index] ?? currency} amount={costPerPerson} />
                      )}
                    </Row>
                  ))}
                </BodyCell>
              </td>
              <td>
                <BodyCell>
                  {totalRows.map((total, index) => (
                    <Row key={`${index}-${total}-${legName}`}>
                      {isLoyalty ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: total }} />
                      ) : (
                        <FormattedCurrency currency={currencyRows?.[index] ?? currency} amount={total} />
                      )}
                    </Row>
                  ))}
                </BodyCell>
              </td>
              <td>
                <BodyCell>
                  {compensationPerPersonRows.map((compensationPerPerson, index) => (
                    <Row key={`${index}-${compensationPerPerson}-${legName}`}>
                      {isLoyalty && !isVoucher ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: compensationPerPerson }} />
                      ) : (
                        <FormattedCurrency currency={currencyRows?.[index] ?? currency} amount={compensationPerPerson} />
                      )}
                    </Row>
                  ))}
                </BodyCell>
              </td>
              <td>
                <BodyCell>
                  {totalCompensationRows.map((totalCompensation, index) => (
                    <Row key={`${index}-${totalCompensation}-${legName}`}>
                      {isLoyalty && !isVoucher ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: totalCompensation }} />
                      ) : (
                        <FormattedCurrency currency={currencyRows?.[index] ?? currency} amount={totalCompensation} />
                      )}
                    </Row>
                  ))}
                </BodyCell>
              </td>
            </tr>
          );
        })}
      </StyledTableBody>
      <StyledTableFooter>
        <tr>
          <td colSpan={5} data-testid="compensation-ticket-price">
            <FormattedMessage id="prrRefund.ticketPrice" />:{' '}
            {isLoyalty ? (
              <FormattedMessage id="searchResults.pts" values={{ points: +ticketPrice.toFixed(2) }} />
            ) : (
              <FormattedCurrency currency={currency} amount={+ticketPrice.toFixed(2)} />
            )}
          </td>
          <td colSpan={2} data-testid="compensation-refund-amount">
            <FormattedMessage id="prrRefund.refundAmount" />:{' '}
            {isLoyalty && !isVoucher ? (
              <FormattedMessage id="searchResults.pts" values={{ points: +refundAmount.toFixed(2) }} />
            ) : (
              <FormattedCurrency currency={currency} amount={+refundAmount.toFixed(2)} />
            )}
          </td>
        </tr>
      </StyledTableFooter>
    </StyledTable>
  );
};

NonSalesforceCompensationBreakdown.propTypes = {
  isVoucher: PropTypes.bool,
  isLoyalty: PropTypes.bool,
  currency: PropTypes.string,
  refundPercentage: PropTypes.string,
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      direction: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      )
    })
  )
};

export default NonSalesforceCompensationBreakdown;
